import { Box, SelectField, Text } from '@factorialco/deprecated-design-system'
import { F0TagRaw, F0Icon } from '@factorialco/f0-react'
import { Sparkles, Check } from '@factorialco/f0-react/icons/app'

import { useEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import PriceInfo from 'modules/upsell/components/SelfUpsell/UpgradePlanModal/PriceInfo'
import type Plan from 'modules/upsell/lib/selfUpsell/plans/Plan'
import {
  BUSINESS_VARIANT,
  ENTERPRISE_VARIANT,
  isOnePlan,
} from 'modules/upsell/lib/selfUpsell/plans/utils'
import type { VariantsEnum } from 'modules/upsell/lib/selfUpsell/types'
import { useSelfUpsellContext } from 'modules/upsell/store/SelfUpsellProvider'

type VariantSelectorProps = {
  addon: Plan
  crossSellingAddon?: Plan | undefined
  showPrice: boolean
  setSelectedAddonId: (id: string) => void
  installedAddonInternalId?: string | null
}

const getDefaultVariant = (plan: Plan): VariantsEnum | undefined => {
  if (plan.variantTypes.includes(ENTERPRISE_VARIANT)) return ENTERPRISE_VARIANT
  if (plan.variantTypes.includes(BUSINESS_VARIANT)) return BUSINESS_VARIANT
  return undefined
}

const matchesInstalledAddonId = (installedAddonId: string, planIds: string[]): boolean => {
  const normalizedInstalledAddonId = installedAddonId.toLowerCase().trim()

  return planIds.some((planId) => {
    const normalizedPlanId = planId.toLowerCase().trim()
    return (
      normalizedPlanId === normalizedInstalledAddonId ||
      normalizedPlanId.includes(normalizedInstalledAddonId) ||
      normalizedInstalledAddonId.includes(normalizedPlanId)
    )
  })
}

const VariantSelector = ({
  addon,
  crossSellingAddon,
  showPrice,
  setSelectedAddonId,
  installedAddonInternalId,
}: VariantSelectorProps) => {
  const {
    allAddons,
    configPlan,
    setConfigPlan,
    period,
    togglePlanSelect,
    currency,
    discount,
    setSeats,
    seats,
    currentSeats,
    isExpenses,
  } = useSelfUpsellContext()
  const isOne = isOnePlan(addon.internalTitle)

  const onePlans = allAddons.f25Addons

  const installedOnePlan = onePlans.find((plan) => {
    if (!installedAddonInternalId) return false

    const planIds = [plan.pricing.getPlanId(period), ...plan.pricing.getAllPlanIds(period)].filter(
      (id): id is string => Boolean(id)
    )

    return matchesInstalledAddonId(installedAddonInternalId, planIds)
  })

  const selectableOnePlans = onePlans.filter(
    (plan) => !installedOnePlan || plan.internalTitle !== installedOnePlan.internalTitle
  )
  const oneSelectorOptions = onePlans.map((plan) => {
    const isCurrentPlan = installedOnePlan?.internalTitle === plan.internalTitle

    return {
      label: isCurrentPlan
        ? `${plan.title} (${i18n.t('marketplace.revamp.in_your_plan')})`
        : plan.title,
      value: plan.internalTitle,
      disabled: isCurrentPlan,
    }
  })

  const handleOnePlanChange = (selectedPlan: Plan) => {
    const defaultVariant = getDefaultVariant(selectedPlan)
    if (!defaultVariant) return

    if (!selectedPlan.isSelected) togglePlanSelect(selectedPlan)

    selectedPlan.setActiveVariant(defaultVariant)
    setSelectedAddonId(selectedPlan.internalTitle)

    const addons = [selectedPlan.pricing.getPlanId(period) || '']
    const crossSellingPlanId = crossSellingAddon?.pricing.getPlanId(period)
    if (crossSellingPlanId) addons.push(crossSellingPlanId)

    setConfigPlan({
      addon: addons,
      variant: defaultVariant,
    })
  }

  useEffect(() => {
    if (isOne && selectableOnePlans.length > 0) {
      const currentPlanIsInstalled = installedOnePlan?.internalTitle === addon.internalTitle
      if (currentPlanIsInstalled && selectableOnePlans[0]) {
        handleOnePlanChange(selectableOnePlans[0])
      }
      return
    }

    const hasEnterprise = addon.variantTypes.includes(ENTERPRISE_VARIANT)
    const hasBusiness = addon.variantTypes.includes(BUSINESS_VARIANT)
    if (!hasEnterprise && !hasBusiness) return

    if (configPlan.variant && addon.variantTypes.includes(configPlan.variant)) return
    if (!addon.isSelected) togglePlanSelect(addon)

    const nextVariant = hasEnterprise ? ENTERPRISE_VARIANT : BUSINESS_VARIANT
    addon.setActiveVariant(nextVariant)
    if (isExpenses()) {
      const enterpriseTieredPrice = addon
        .getSpecificVariant(nextVariant)
        ?.pricing.getTieredPrice(period, seats)
      setSeats(enterpriseTieredPrice?.tiers?.[0]?.endingUnit || 1)
    }

    const addons = [...addon.pricing.getAllPlanIds(period)]
    const crossSellingPlanId = crossSellingAddon?.pricing.getPlanId(period)
    if (crossSellingPlanId) {
      addons.push(crossSellingPlanId)
    }

    setConfigPlan({
      addon: addons,
      variant: nextVariant,
    })
  }, [])

  if (isOne) {
    const selectedOneVariant = getDefaultVariant(addon)

    return (
      <Box gap="s6" width="full" style={{ minWidth: '0' }}>
        <Text.Normal size="100" color="grey700" weight="medium">
          {isOne
            ? i18n.t('growth.self_upsell.one_selector')
            : i18n.t('self_upsell.first_step.select_plan_type')}
        </Text.Normal>
        <Box gap="s8" width="full" style={{ minWidth: '0' }}>
          {oneSelectorOptions.length > 0 && (
            <Box width="full" style={{ minWidth: '0', maxWidth: '100%' }}>
              <SelectField
                hideLabel
                options={oneSelectorOptions}
                value={addon.internalTitle}
                onChange={(selectedId) => {
                  const selectedPlan = onePlans.find((plan) => plan.internalTitle === selectedId)
                  if (selectedPlan?.internalTitle === installedOnePlan?.internalTitle) return
                  if (!selectedPlan) return
                  handleOnePlanChange(selectedPlan)
                }}
                selectValue={addon.internalTitle}
                missingLabel={i18n.t('self_upsell.first_step.select_plan_type')}
              />
            </Box>
          )}

          {selectedOneVariant && (
            <Box
              border={{
                all: {
                  width: 's1',
                  style: 'solid',
                  color: 'viridian1000',
                },
              }}
              borderRadius={{ all: 'abs016' }}
              background={'viridian100'}
              padding="s16"
              gap="s8"
              justifyContent="spaceBetween"
              style={{
                height: '88px',
              }}
            >
              <Box flexDirection="row" justifyContent="spaceBetween" alignItems="flexStart">
                <Text.Normal size="200" weight="semibold" color="grey1000">
                  {addon.title}
                </Text.Normal>
              </Box>
              {showPrice && (
                <PriceInfo
                  variant={addon.getSpecificVariant(selectedOneVariant)}
                  period={period}
                  currency={currency}
                  seats={seats}
                  currentSeats={currentSeats}
                  discount={discount}
                  disabled={false}
                />
              )}
            </Box>
          )}
        </Box>
      </Box>
    )
  }

  return (
    <Box gap="s6">
      <Text.Normal size="100" color="grey700" weight="medium">
        {i18n.t('self_upsell.first_step.select_plan_type')}
      </Text.Normal>
      <Box gap="s8">
        <Box
          border={{
            all: {
              width: 's1',
              style: 'solid',
              color: configPlan.variant === ENTERPRISE_VARIANT ? 'viridian1000' : 'grey400',
            },
          }}
          borderRadius={{ all: 'abs016' }}
          background={configPlan.variant === ENTERPRISE_VARIANT ? 'viridian100' : 'grey000White'}
          padding="s16"
          gap="s8"
          justifyContent="spaceBetween"
          style={{
            height: '88px',
          }}
        >
          <Box flexDirection="row" justifyContent="spaceBetween" alignItems="flexStart">
            <Text.Normal size="200" weight="semibold" color="grey1000">
              {ENTERPRISE_VARIANT}
            </Text.Normal>
            <F0TagRaw text="Best value" icon={Sparkles} />
          </Box>
          {showPrice && (
            <PriceInfo
              variant={addon.getSpecificVariant(ENTERPRISE_VARIANT)}
              period={period}
              currency={currency}
              seats={seats}
              currentSeats={currentSeats}
              discount={discount}
              disabled={false}
            />
          )}
        </Box>

        <Box
          border={{
            all: {
              width: 's1',
              style: 'solid',
              color: 'grey400',
            },
          }}
          borderRadius={{ all: 'abs016' }}
          background={'grey000White'}
          padding="s16"
          gap="s8"
          justifyContent="spaceBetween"
          style={{
            height: '88px',
          }}
        >
          <Box flexDirection="row" justifyContent="spaceBetween" alignItems="flexStart">
            <Text.Normal size="200" weight="semibold" color="grey600">
              {BUSINESS_VARIANT}
            </Text.Normal>
            <Box className="text-color-grey flex-row items-center pr-2">
              <F0Icon icon={Check} color="secondary" width="16px" height="16px" />
              <Text.Normal size="200" color="grey600">
                {i18n.t('marketplace.revamp.in_your_plan')}
              </Text.Normal>
            </Box>
          </Box>
          {showPrice && (
            <PriceInfo
              variant={addon.getSpecificVariant(BUSINESS_VARIANT)}
              period={period}
              currency={currency}
              seats={seats}
              currentSeats={currentSeats}
              discount={discount}
              disabled
            />
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default VariantSelector
